/**
 * The production `DomainApi` implementation (ADR-0001 `src/data` ownership):
 * wires the pure domain layer (`src/domain`) to the Dexie cache and sync
 * queue (`src/data/cache`, `src/data/sync`) behind the frozen contract
 * (`domain-api.ts`). This is what `src/app/main.tsx` composes at startup in
 * place of dev-ui's in-memory reference fixture
 * (`src/ui/testing/realDomain.ts`) -- see that file's and main.tsx's
 * top-of-file notes for the swap-in expectation.
 *
 * Scope (CLAUDE.md / phase-1-mvp.md): only objectives/actions/forecast/
 * Today-view/sync/export/onboarding are implemented. Every habit/reward/
 * badge/weekly-review method is Phase 2 and throws a `DomainErrorInfo` with
 * message prefix `not_implemented_phase1` (matching the convention already
 * established in `src/ui/testing/realDomain.ts`, since `domain-api.ts` has
 * no dedicated "not implemented" error code) -- Phase 1's UI never calls
 * these paths per the phase spec's scope boundary.
 *
 * Every query is a fresh derivation from `cache.allEvents()` -- no cached
 * view-model state -- matching "pure function of the event log" (PRD §9,
 * ADR-0002). At single-user scale this is cheap; if it ever isn't, a
 * memoization layer would go here without changing the public shape.
 */

import type {
  ActionId,
  AdjustmentSuggestionView,
  BadgeDraft,
  BadgeId,
  BadgeView,
  CelebrationCue,
  ClockPort,
  CompassEvent,
  CompletionHistoryMonthSummaryView,
  CompletionHistoryMonthView,
  DailyPageView,
  DisplayTimeLabel,
  DomainApi,
  DomainErrorInfo,
  EventCachePort,
  EventId,
  ExportFile,
  HabitCategory,
  HabitDraft,
  HabitId,
  HabitView,
  IdeaCategory,
  IdeaCountsView,
  IdeaId,
  IdeaView,
  IdPort,
  ImportSummary,
  IsoDate,
  ActionStatus,
  LifeAreaTag,
  NextUpView,
  NoteId,
  NotePromotionTarget,
  NoteView,
  ObjectiveCategory,
  ObjectiveCheckpointAnswer,
  ObjectiveDateSuggestionView,
  ObjectiveDraft,
  ObjectiveId,
  ObjectiveLevelOfEffort,
  ObjectivePriority,
  ObjectiveReminder,
  ObjectiveStatus,
  ObjectiveSummaryView,
  ObjectiveView,
  PinnedObjectiveView,
  PriorityActionLink,
  PriorityId,
  PriorityView,
  Rating,
  ReviewCompletedEvent,
  RewardConfig,
  RewardSnapshot,
  RewardsExplainerView,
  SentenceOfDayState,
  SyncStatusView,
  SyncTransportPort,
  TargetAdjustment,
  TrailingAnalyticsView,
  WeeklyReviewView,
  WeekStart,
  YearMonth,
} from '../../contracts';
import { getTrailingAnalytics as getTrailingAnalyticsReal } from '../../domain/today/analytics';
import {
  createIdea as createIdeaCommand,
  deleteIdea as deleteIdeaCommand,
  getIdeaCounts as getIdeaCountsReal,
  listIdeas as listIdeasReal,
  promoteIdea as promoteIdeaCommand,
} from '../../domain/capture/ideas';
import {
  createNote as createNoteCommand,
  deleteNote as deleteNoteCommand,
  editNote as editNoteCommand,
  listNotes as listNotesReal,
  promoteNote as promoteNoteCommand,
} from '../../domain/capture/notes';
import { computeDailyFields } from '../../domain/checkin/daily-fields';
import { dedupeEvents, eventsOfTypes, excludingReverted } from '../../domain/event-log/event-log';
import {
  computeCompletionHistoryMonth,
  computeCompletionHistoryYearSummary,
} from '../../domain/history/completion-history';
import { addDaysToDate } from '../../domain/habits/week';
import {
  adjustTargetManually as adjustTargetManuallyCommand,
  archiveHabit as archiveHabitCommand,
  completeCheckin as completeCheckinCommand,
  completeHabit as completeHabitCommand,
  createHabit as createHabitCommand,
  editHabit as editHabitCommand,
  logClean as logCleanCommand,
  logLapse as logLapseCommand,
  pauseHabit as pauseHabitCommand,
  resumeHabit as resumeHabitCommand,
  skipHabit as skipHabitCommand,
} from '../../domain/habits/commands';
import { computeDueHabits, projectHabitView, projectHabitViews } from '../../domain/habits/projection';
import {
  acceptObjectiveDateSuggestion as acceptObjectiveDateSuggestionCommand,
  addAction as addActionCommand,
  adjustObjectiveTargetDateManually as adjustObjectiveTargetDateManuallyCommand,
  archiveObjective as archiveObjectiveCommand,
  assertValidRecordingDate,
  completeAction as completeActionCommand,
  createObjective as createObjectiveCommand,
  editAction as editActionCommand,
  editObjective as editObjectiveCommand,
  linkObjectives as linkObjectivesCommand,
  removeAction as removeActionCommand,
  reorderActions as reorderActionsCommand,
  revertLogEntry as revertLogEntryCommand,
  setActionStatus as setActionStatusCommand,
  setObjectivePinned as setObjectivePinnedCommand,
  setObjectiveStatus as setObjectiveStatusCommand,
  unlinkObjectives as unlinkObjectivesCommand,
  updateObjectiveDetails as updateObjectiveDetailsCommand,
} from '../../domain/objectives/commands';
import {
  projectObjectiveSummaries,
  projectObjectiveView,
  projectPinnedObjectives,
} from '../../domain/objectives/projection';
import {
  completeStandalonePriority as completeStandalonePriorityCommand,
  createPriority as createPriorityCommand,
  listPriorities as listPrioritiesReal,
  removePriority as removePriorityCommand,
  resolvePriorityCompletionPath,
} from '../../domain/today/priorities';
import {
  computeDayClosed,
  computeFavoriteMoment,
  computeGratitude,
  computeSleepHours,
  closeDay as closeDayCommand,
  setFavoriteMoment as setFavoriteMomentCommand,
  setGratitudeSlot as setGratitudeSlotCommand,
  setSleepHours as setSleepHoursCommand,
} from '../../domain/today/reflection';
import { getNextUp as getNextUpReal } from '../../domain/today/due-actions';
import {
  getSentenceOfDayState as getSentenceOfDayStateReal,
  saveQuote as saveQuoteCommand,
} from '../../domain/today/sentence-of-day';
import {
  acceptSuggestion as acceptSuggestionCommand,
  answerObjectiveCheckpoint as answerObjectiveCheckpointCommand,
} from '../../domain/review/commands';
import { computeCurrentReviewWeek, computeWeeklyReview } from '../../domain/review/weekly-review';
import { evaluateBadges } from '../../domain/rewards/badges';
import {
  archiveBadge as archiveBadgeCommand,
  assembleLiveBadges,
  createBadge as createBadgeCommand,
  editBadge as editBadgeCommand,
  isStarterBadge,
  resetRewardConfig as resetRewardConfigCommand,
  updateRewardConfig as updateRewardConfigCommand,
} from '../../domain/rewards/commands';
import { resolveRewardConfig } from '../../domain/rewards/config';
import {
  computeCelebrationCue,
  computeRewardSnapshot,
  computeRewardsExplainer,
} from '../../domain/rewards/snapshot';
import { computeDailyProgress, computeDueActions } from '../../domain/today/due-actions';
import { createSyncQueueDriver, type SyncQueueDriver } from '../sync/outbound-queue';

const VALID_REVERT_TARGET_TYPES: ReadonlySet<CompassEvent['type']> = new Set([
  'habit.completed',
  'habit.skipped',
  'habit.clean_logged',
  'habit.lapse_logged',
  'study.session_logged',
  'action.completed',
  // The one-click accept for a forecast date suggestion (P1-18, AC-7) must
  // be undoable like any other logged entry -- ADR-0002's revert primitive
  // is generic over any target event, and objectives/state.ts's
  // resolveEffectiveTargetDate already treats a reverted
  // objective.target_date_adjusted as absent (mirrors action.completed).
  'objective.target_date_adjusted',
  // Un-archiving (ADR-0011 Decision 3 / phase-2d-management-history.md MC-3):
  // there is no dedicated unarchiveObjective command -- reverting the
  // objective.archived event is the mechanism, and objectives/state.ts's
  // resolveEffectiveArchived already treats a reverted archive as absent.
  'objective.archived',
  // Mood/energy taps (Phase 3, ADR-0002 addendum 2026-07-03, ME-4/AC-P9):
  // one-tap-undo must work on these exactly like every other logged entry --
  // computeDailyFields already treats a reverted checkin.mood_set/energy_set
  // as absent (falls back to the prior surviving tap, or null), mirroring
  // objective.target_date_adjusted's precedent above. checkin.note_set is
  // deliberately NOT added: the daily note field stays WON'T-for-now (PRD),
  // and adding an allowlist entry for a field with no writable command yet
  // would be dead code.
  'checkin.mood_set',
  'checkin.energy_set',
  // Phase 5 (ADR-0002 addendum 2026-07-04 D1/D3): the new state-machine
  // events are revertible exactly like every other logged entry --
  // objectives/state.ts's resolveEffectiveState/computeActionDisplayStatus
  // already treat a reverted status_changed as absent (mirrors
  // objective.archived/objective.target_date_adjusted's precedent above).
  'objective.status_changed',
  'action.status_changed',
  // Phase 6 (ADR-0002 addendum 2026-07-07, flagged in the addendum's own
  // "required implementation-layer allowlist additions" note): standalone-
  // Priority undo (priority.completed -- linked-Priority undo already works
  // via the existing action.completed entry above, no separate addition
  // needed for that case) and close-the-day undo (day.closed). Confirmed
  // against the precedent question the addendum raised (whether
  // review.completed's own omission from this allowlist was deliberate):
  // review.completed is NOT here today and there is no revert path for it
  // anywhere in the product (no "un-complete review" affordance exists) --
  // day.closed is added here because AC-T22/the Today header's "Close the
  // day" action IS specified as one-tap-revertible like every other logged
  // entry on that page (mirrors checkin.completed's own revertibility, not
  // review.completed's non-revertibility), so the two rituals genuinely
  // diverge on this point rather than day.closed silently matching
  // whichever precedent happened to be simplest.
  'priority.completed',
  'day.closed',
]);

class DomainError extends Error implements DomainErrorInfo {
  readonly code: DomainErrorInfo['code'];
  constructor(info: DomainErrorInfo) {
    super(info.message);
    this.code = info.code;
    this.name = 'DomainError';
  }
}

function notImplementedPhase1(code: DomainErrorInfo['code'], what: string): never {
  throw new DomainError({
    code,
    message: `not_implemented_phase1: ${what} is Phase 2`,
  });
}

export interface CreateDomainApiOptions {
  readonly cache: EventCachePort;
  readonly clock: ClockPort;
  readonly id: IdPort;
  readonly transport: SyncTransportPort;
  /** Injected for tests; production callers use the real timer-based defaults. */
  readonly syncQueue?: SyncQueueDriver;
  /** How many rows the Today view surfaces; ux-designer's screen spec sets the real number. */
  readonly dueActionsLimit?: number;
}

/** Internal result shape; `createDomainApi` narrows to just `api` for the public contract. */
interface DomainApiWithTestHooks {
  readonly api: DomainApi;
  /** Resolves once every fire-and-forget push triggered so far has settled. Test/tooling only. */
  readonly waitForPendingSync: () => Promise<void>;
}

export function createDomainApi(options: CreateDomainApiOptions): DomainApi {
  return buildDomainApi(options).api;
}

/**
 * Same construction as `createDomainApi`, plus `waitForPendingSync` for
 * deterministic test teardown -- production code (`src/app/main.tsx`) has
 * no need for this and continues to use the plain `createDomainApi`.
 * Without draining fire-and-forget pushes between test cases, concurrent
 * un-awaited `pushAll()` calls from several commands issued in the same
 * test file keep running past the point the test considers the interaction
 * "done", which both wastes time and destabilizes coverage-instrumented
 * runs (observed: v8 coverage temp-file teardown racing a still-running
 * push loop).
 */
export function createDomainApiForTesting(
  options: CreateDomainApiOptions,
): DomainApiWithTestHooks {
  return buildDomainApi(options);
}

function buildDomainApi(options: CreateDomainApiOptions): DomainApiWithTestHooks {
  const { cache, clock, id } = options;
  const ports = { clock, id };
  const listeners = new Set<() => void>();
  const dueActionsLimit = options.dueActionsLimit ?? 5;

  const syncQueue =
    options.syncQueue ??
    createSyncQueueDriver({ cache, transport: options.transport });

  function notify(): void {
    for (const listener of listeners) listener();
  }

  // Tracks the in-flight fire-and-forget push triggered by the most recent
  // append, so tests (and any caller that genuinely needs to know sync has
  // settled) can await it deterministically via `waitForPendingSync` below,
  // without the tap path itself ever blocking on it (ADR-0004: "the network
  // is never on the tap path"). Without this, concurrent un-awaited pushAll
  // calls across many commands in quick succession (typical in a test that
  // issues several commands back to back) keep running past the point the
  // caller considers the interaction "done" -- harmless in the browser,
  // but a source of leaked async work and flaky teardown in tests.
  let pendingSyncTail: Promise<void> = Promise.resolve();

  async function append(events: readonly CompassEvent[]): Promise<void> {
    await cache.appendLocal(events);
    notify();
    // Fire-and-forget: the tap/command path never waits on the network
    // (ADR-0004 "the network is never on the tap path"). Errors are
    // reflected in getSyncStatus(), not thrown from the command; chained
    // (not parallel) so pushes stay ordered and `waitForPendingSync` only
    // ever needs to await one promise.
    pendingSyncTail = pendingSyncTail
      .catch(() => undefined)
      .then(() => syncQueue.pushAll());
  }

  /** Test/tooling hook: resolves once every fire-and-forget push triggered so far has settled. */
  async function waitForPendingSync(): Promise<void> {
    await pendingSyncTail.catch(() => undefined);
  }

  async function allEvents(): Promise<readonly CompassEvent[]> {
    return cache.allEvents();
  }

  // ---------------------------------------------------------------------
  // Queries
  // ---------------------------------------------------------------------

  async function getDailyPage(date: IsoDate): Promise<DailyPageView> {
    const events = await allEvents();
    const today = clock.today();
    const yesterday = addDaysToDate(today, -1) as IsoDate;
    // ADR-0006 addendum (2026-07-03): isBackfill must be "any non-today date
    // reached through a valid backfill navigation", not hardcoded to
    // "exactly yesterday" -- the latter silently under-reports once the
    // recording-date window widens beyond yesterday (MS5). Mirrors
    // src/ui/testing/realDomain.ts's own getDailyPage, which already used
    // this form; production is the one being brought in line here.
    const isBackfill = date !== today;

    const checkinCompletions = excludingReverted(
      eventsOfTypes(events, ['checkin.completed', 'log_entry.reverted']),
    ).filter((e) => e.type === 'checkin.completed' && e.occurredOn === yesterday);
    const backfillAvailable = date === today && checkinCompletions.length === 0;
    const progress = computeDailyProgress(events, date);
    const config = resolveRewardConfig(events);

    // AC-B14/AC-B15: checkinCompleted reflects THIS page's date, not
    // necessarily today -- a backfill (yesterday) read must show yesterday's
    // own completion state, matching `dueActions`/`habits` which are already
    // date-scoped to `date` rather than hardcoded to today.
    const checkinCompletedForDate = excludingReverted(
      eventsOfTypes(events, ['checkin.completed']),
    ).some((e) => e.occurredOn === date);

    // Phase 3 MS3 (ADR-0002 addendum 2026-07-03): last-event-wins mood/
    // energy/note derivation for THIS page's date -- previously hardcoded
    // literals (null/null/''), never actually read from the log.
    const dailyFields = computeDailyFields(events, date);

    // Phase 6 (ADR-0002 addendum 2026-07-07): sleep hours / favorite moment /
    // gratitude / day-closed, all date-scoped scalars mirroring mood/energy/
    // note's placement on this same view.
    const sleepHours = computeSleepHours(events, date);
    const favoriteMoment = computeFavoriteMoment(events, date);
    const gratitude = computeGratitude(events, date);
    const dayClosed = computeDayClosed(events, date);

    return {
      date,
      isBackfill,
      backfillAvailable,
      habits: computeDueHabits(events, date),
      dueActions: computeDueActions(events, date, dueActionsLimit),
      totalConsideredToday: progress.totalConsideredToday,
      completedToday: progress.completedToday,
      mood: dailyFields.mood,
      energy: dailyFields.energy,
      moodEventId: dailyFields.moodEventId,
      energyEventId: dailyFields.energyEventId,
      note: dailyFields.note,
      checkinCompleted: checkinCompletedForDate,
      reward: computeRewardSnapshot(events, config, today),
      sleepHours: sleepHours.hours,
      sleepHoursEventId: sleepHours.eventId,
      favoriteMoment,
      gratitude,
      dayClosed: dayClosed.closed,
      dayClosedEventId: dayClosed.eventId,
    };
  }

  async function getWeeklyReview(weekStart: WeekStart): Promise<WeeklyReviewView> {
    const events = await allEvents();
    return computeWeeklyReview(events, weekStart, clock.today());
  }

  async function getCurrentReviewWeek(): Promise<WeekStart> {
    const events = await allEvents();
    return computeCurrentReviewWeek(events, clock.today());
  }

  async function listHabits(options?: {
    readonly includeArchived?: boolean;
  }): Promise<readonly HabitView[]> {
    const events = await allEvents();
    return projectHabitViews(events, options);
  }

  async function listObjectives(opts?: {
    readonly includeArchived?: boolean;
  }): Promise<readonly ObjectiveSummaryView[]> {
    const events = await allEvents();
    return projectObjectiveSummaries(events, clock.today(), opts);
  }

  async function getObjective(objectiveId: ObjectiveId): Promise<ObjectiveView> {
    const events = await allEvents();
    const view = projectObjectiveView(events, objectiveId, clock.today());
    if (!view) {
      throw new DomainError({
        code: 'objective_not_found',
        message: `objective ${objectiveId} not found`,
      });
    }
    return view;
  }

  async function listPinnedObjectives(): Promise<readonly PinnedObjectiveView[]> {
    const events = await allEvents();
    return projectPinnedObjectives(events);
  }

  async function listBadges(): Promise<readonly BadgeView[]> {
    const events = await allEvents();
    const liveBadges = assembleLiveBadges(events);
    const evaluations = evaluateBadges(liveBadges, events, clock.today());
    const stateByBadgeId = new Map(evaluations.map((e) => [e.badgeId, e.state]));

    return liveBadges.map((badge) => ({
      badgeId: badge.badgeId,
      name: badge.name,
      iconId: badge.iconId,
      criterion: badge.criterion,
      // Every badge in liveBadges was just evaluated above, so this is
      // always present; the fallback is unreachable but keeps the mapping
      // total without a non-null assertion.
      state: stateByBadgeId.get(badge.badgeId) ?? {
        earned: false,
        progress: { current: 0, target: 1 },
      },
      isStarter: isStarterBadge(badge.badgeId),
    }));
  }

  async function getRewardSnapshot(): Promise<RewardSnapshot> {
    const events = await allEvents();
    const config = resolveRewardConfig(events);
    return computeRewardSnapshot(events, config, clock.today());
  }

  async function getRewardsExplainer(): Promise<RewardsExplainerView> {
    const events = await allEvents();
    const config = resolveRewardConfig(events);
    return computeRewardsExplainer(config);
  }

  async function getRewardConfig(): Promise<RewardConfig> {
    const events = await allEvents();
    return resolveRewardConfig(events);
  }

  async function getCompletionHistory(
    month: YearMonth,
  ): Promise<CompletionHistoryMonthView> {
    const events = await allEvents();
    return computeCompletionHistoryMonth(events, month);
  }

  async function getCompletionHistoryYearSummary(
    throughMonth: YearMonth,
  ): Promise<readonly CompletionHistoryMonthSummaryView[]> {
    const events = await allEvents();
    return computeCompletionHistoryYearSummary(events, throughMonth);
  }

  function getSyncStatus(): SyncStatusView {
    const status = syncQueue.getStatus();
    return {
      state: status.state,
      pendingEvents: status.pendingEvents,
      lastSyncedAt: status.lastSyncedAt as SyncStatusView['lastSyncedAt'],
    };
  }

  async function isOnboardingNeeded(): Promise<boolean> {
    const events = await allEvents();
    return !events.some((e) => e.type === 'onboarding.completed');
  }

  // ---------------------------------------------------------------------
  // Check-in commands (HS3, phase-2b-habits.md)
  // ---------------------------------------------------------------------

  /** Looks up a habit's category for `CelebrationCue.category` (PRD §8 "one accent per habit category"). */
  function categoryOf(events: readonly CompassEvent[], habitId: HabitId): HabitCategory | undefined {
    return projectHabitView(events, habitId)?.category;
  }

  async function completeHabit(habitId: HabitId, date: IsoDate): Promise<CelebrationCue> {
    const events = await allEvents();
    assertValidRecordingDate(date, clock);
    const { events: newEvents } = completeHabitCommand(events, habitId, date, ports);
    const config = resolveRewardConfig(events);
    const liveBadges = assembleLiveBadges(events);
    const habitCategory = categoryOf(events, habitId);
    await append(newEvents);
    // AC-B7: build-habit completion earns xp.habitCompleted; AC-B14/AC-B15:
    // the same batch may also carry an implicit checkin.completed (first tap
    // of the day), whose xp.checkinCompleted is folded into the SAME cue --
    // commands.ts's withImplicitCheckin already appended both events, so
    // comparing before/after XP totals here picks up both awards in one cue
    // without any special-casing (RS3-style "diff the whole batch" pattern).
    return computeCelebrationCue(events, newEvents, config, clock.today(), {
      badges: liveBadges,
      habitCategory,
    });
  }

  async function skipHabit(habitId: HabitId, date: IsoDate): Promise<void> {
    const events = await allEvents();
    assertValidRecordingDate(date, clock);
    const { events: newEvents } = skipHabitCommand(events, habitId, date, ports);
    await append(newEvents);
  }

  async function logClean(habitId: HabitId, date: IsoDate): Promise<CelebrationCue> {
    const events = await allEvents();
    assertValidRecordingDate(date, clock);
    const { events: newEvents } = logCleanCommand(events, habitId, date, ports);
    const config = resolveRewardConfig(events);
    const liveBadges = assembleLiveBadges(events);
    const habitCategory = categoryOf(events, habitId);
    await append(newEvents);
    // AC-B11: the explicit "Clean today" tap earns xp.avoidCleanDay
    // immediately (plus a possible implicit checkinCompleted, same batching
    // as completeHabit above).
    return computeCelebrationCue(events, newEvents, config, clock.today(), {
      badges: liveBadges,
      habitCategory,
    });
  }

  /**
   * Judgment-free by contract (R7.7/AC-B12): never returns a CelebrationCue
   * (no XP, no celebration for a lapse) even though `logLapseCommand` may
   * also append an implicit `checkin.completed` (a lapse still counts as
   * "showed up", AC-B14) -- that XP is silently folded into the NEXT
   * `getRewardSnapshot()`/`getDailyPage()` read rather than surfaced via a
   * cue here, matching the contract's own doc comment on `logLapse`: "never
   * returns a cue, never subtracts."
   */
  async function logLapse(habitId: HabitId, date: IsoDate): Promise<void> {
    const events = await allEvents();
    assertValidRecordingDate(date, clock);
    const { events: newEvents } = logLapseCommand(events, habitId, date, ports);
    await append(newEvents);
  }

  async function logStudySession(): Promise<void> {
    // HB-10/phase-2b-habits.md §1b item 6: study-session capture (timer/
    // manual minutes) is Phase 3 scope -- a study-category habit completes
    // with a bare completeHabit tap like any other build habit this round.
    notImplementedPhase1('habit_not_found', 'study session logging');
  }

  async function revertLogEntry(eventId: EventId): Promise<void> {
    const events = await allEvents();
    const target = events.find((e) => e.eventId === eventId);
    if (!target) {
      throw new DomainError({
        code: 'not_revertible',
        message: `event ${eventId} not found`,
      });
    }
    if (!VALID_REVERT_TARGET_TYPES.has(target.type)) {
      throw new DomainError({
        code: 'not_revertible',
        message: `event of type ${target.type} is not a revertible entry`,
      });
    }
    const { events: newEvents } = revertLogEntryCommand(eventId, ports);
    await append(newEvents);
  }

  /**
   * ME-1 (docs/specs/phase-3-plan.md §3): appends checkin.mood_set, gated by
   * the SAME assertValidRecordingDate window every other Today-view command
   * uses (backfilled mood capture follows the same rule as backfilled
   * completions, not a separate one). No XP (ME-5/AC-P10) -- mood/energy are
   * absent from xp.ts's XpAward sources by construction, so there is nothing
   * to award here; the function's void return type reflects that directly
   * rather than returning a manufactured zero-XP CelebrationCue.
   */
  async function setMood(date: IsoDate, mood: Rating): Promise<void> {
    assertValidRecordingDate(date, clock);
    const event = {
      eventId: id.nextEventId(),
      type: 'checkin.mood_set' as const,
      schemaVersion: 1 as const,
      occurredOn: date,
      recordedAt: clock.now(),
      mood,
    };
    await append([event]);
  }
  async function setEnergy(date: IsoDate, energy: Rating): Promise<void> {
    assertValidRecordingDate(date, clock);
    const event = {
      eventId: id.nextEventId(),
      type: 'checkin.energy_set' as const,
      schemaVersion: 1 as const,
      occurredOn: date,
      recordedAt: clock.now(),
      energy,
    };
    await append([event]);
  }
  /**
   * R8.5/D6, Phase 6 (ADR-0002 addendum 2026-07-07): the daily note field's
   * PRD v0.10 WON'T-for-now status is LIFTED for this one field, per the
   * addendum's explicit ruling -- reuses `checkin.note_set` VERBATIM (no new
   * event type; confirmed no duplicate note event exists anywhere in
   * `events.ts`'s union). Mirrors `setMood`/`setEnergy` exactly: gated by the
   * same `assertValidRecordingDate` backfill window, no XP (note-setting is
   * absent from `xp.ts`'s award sources by construction, same as mood/
   * energy), empty string clears (matches the event's own doc comment).
   */
  async function setNote(date: IsoDate, note: string): Promise<void> {
    assertValidRecordingDate(date, clock);
    const event = {
      eventId: id.nextEventId(),
      type: 'checkin.note_set' as const,
      schemaVersion: 1 as const,
      occurredOn: date,
      recordedAt: clock.now(),
      note,
    };
    await append([event]);
  }

  /**
   * The EXPLICIT check-in-completion path (contract: `completeCheckin`).
   * This phase's UI never calls it directly (§2.2 reasoning point 3: no new
   * "Done for today" button this round) -- `checkin.completed` normally
   * arrives implicitly via `withImplicitCheckin` inside the logging
   * commands above (AC-B14). This function stays real (not a stub) because
   * the contract requires it and a future "close the day" button
   * (phase-2b-habits.md §7 item 1's flagged alternative) can call it with
   * no domain change. AC-B15's collapse rule applies identically here: a
   * second call the same day appends another event but earns no further XP
   * (xp.ts's computeCheckinXpAwards de-dupes by occurredOn).
   */
  async function completeCheckin(date: IsoDate): Promise<CelebrationCue> {
    const events = await allEvents();
    assertValidRecordingDate(date, clock);
    const { events: newEvents } = completeCheckinCommand(events, date, ports);
    const config = resolveRewardConfig(events);
    const liveBadges = assembleLiveBadges(events);
    await append(newEvents);
    return computeCelebrationCue(events, newEvents, config, clock.today(), {
      badges: liveBadges,
    });
  }

  // ---------------------------------------------------------------------
  // Habit management (HS1, phase-2b-habits.md)
  // ---------------------------------------------------------------------

  async function createHabit(draft: HabitDraft): Promise<HabitId> {
    const { events: newEvents } = createHabitCommand(draft, ports);
    await append(newEvents);
    const created = newEvents[0];
    if (!created || created.type !== 'habit.created') {
      throw new DomainError({ code: 'invalid_input', message: 'habit creation failed' });
    }
    return created.habitId;
  }

  async function editHabit(
    habitId: HabitId,
    changes: Parameters<DomainApi['editHabit']>[1],
  ): Promise<void> {
    const events = await allEvents();
    const { events: newEvents } = editHabitCommand(events, habitId, changes, ports);
    await append(newEvents);
  }

  async function adjustTargetManually(
    habitId: HabitId,
    adjustment: TargetAdjustment,
  ): Promise<void> {
    const events = await allEvents();
    const { events: newEvents } = adjustTargetManuallyCommand(events, habitId, adjustment, ports);
    await append(newEvents);
  }

  async function pauseHabit(habitId: HabitId): Promise<void> {
    const events = await allEvents();
    const { events: newEvents } = pauseHabitCommand(events, habitId, ports);
    await append(newEvents);
  }

  async function resumeHabit(habitId: HabitId): Promise<void> {
    const events = await allEvents();
    const { events: newEvents } = resumeHabitCommand(events, habitId, ports);
    await append(newEvents);
  }

  async function archiveHabit(habitId: HabitId): Promise<void> {
    const events = await allEvents();
    const { events: newEvents } = archiveHabitCommand(events, habitId, ports);
    await append(newEvents);
  }

  // ---------------------------------------------------------------------
  // Objectives (P1-1..P1-5, P1-17/P1-18)
  // ---------------------------------------------------------------------

  async function createObjective(draft: ObjectiveDraft): Promise<ObjectiveId> {
    const events = await allEvents();
    const { events: newEvents } = createObjectiveCommand(events, draft, ports);
    await append(newEvents);
    const created = newEvents[0];
    if (!created || created.type !== 'objective.created') {
      throw new DomainError({ code: 'invalid_input', message: 'objective creation failed' });
    }
    return created.objectiveId;
  }

  async function editObjective(
    objectiveId: ObjectiveId,
    changes: { readonly title?: string; readonly why?: string },
  ): Promise<void> {
    const events = await allEvents();
    const { events: newEvents } = editObjectiveCommand(events, objectiveId, changes, ports);
    await append(newEvents);
  }

  async function adjustObjectiveTargetDateManually(
    objectiveId: ObjectiveId,
    nextTargetDate: IsoDate,
  ): Promise<void> {
    const events = await allEvents();
    const { events: newEvents } = adjustObjectiveTargetDateManuallyCommand(
      events,
      objectiveId,
      nextTargetDate,
      ports,
    );
    await append(newEvents);
  }

  async function archiveObjective(objectiveId: ObjectiveId): Promise<void> {
    const events = await allEvents();
    const { events: newEvents } = archiveObjectiveCommand(events, objectiveId, ports);
    await append(newEvents);
  }

  async function addAction(
    objectiveId: ObjectiveId,
    input: {
      readonly title: string;
      readonly dueDate: IsoDate | null;
      readonly milestoneTag: string | null;
      readonly position: number | null;
    },
  ) {
    const events = await allEvents();
    const { events: newEvents } = addActionCommand(events, objectiveId, input, ports);
    await append(newEvents);
    const added = newEvents[0];
    if (!added || added.type !== 'action.added') {
      throw new DomainError({ code: 'invalid_input', message: 'action creation failed' });
    }
    return added.actionId;
  }

  async function editAction(
    objectiveId: ObjectiveId,
    actionId: Parameters<DomainApi['editAction']>[1],
    changes: Parameters<DomainApi['editAction']>[2],
  ): Promise<void> {
    const events = await allEvents();
    const { events: newEvents } = editActionCommand(events, objectiveId, actionId, changes, ports);
    await append(newEvents);
  }

  async function removeAction(
    objectiveId: ObjectiveId,
    actionId: Parameters<DomainApi['removeAction']>[1],
  ): Promise<void> {
    const events = await allEvents();
    const { events: newEvents } = removeActionCommand(events, objectiveId, actionId, ports);
    await append(newEvents);
  }

  async function completeAction(
    objectiveId: ObjectiveId,
    actionId: Parameters<DomainApi['completeAction']>[1],
    date: IsoDate,
  ): Promise<CelebrationCue> {
    const events = await allEvents();
    assertValidRecordingDate(date, clock);
    const { events: newEvents } = completeActionCommand(events, objectiveId, actionId, date, ports);
    const config = resolveRewardConfig(events);
    const liveBadges = assembleLiveBadges(events);
    await append(newEvents);
    // Rewards v1 (docs/specs/phase-2a-rewards.md RW-1): action completion
    // earns xp.actionCompleted via the reward engine, activating what was a
    // neutral Phase-1 stub. Also retroactively pays out for every
    // Phase-1-era action.completed event once the config/badge derivation
    // runs over the full log (ADR-0002 readiness-audit addendum: "the
    // moment the Phase 2 reward engine ships... retroactively earn XP").
    return computeCelebrationCue(events, newEvents, config, clock.today(), { badges: liveBadges });
  }

  async function reorderActions(
    objectiveId: ObjectiveId,
    orderedActionIds: Parameters<DomainApi['reorderActions']>[1],
  ): Promise<void> {
    const events = await allEvents();
    const { events: newEvents } = reorderActionsCommand(
      events,
      objectiveId,
      orderedActionIds,
      ports,
    );
    await append(newEvents);
  }

  // Phase 5 (ADR-0002 addendum 2026-07-04): thin wrappers over the new
  // domain commands, mirroring every wrapper above -- read the log, call
  // the pure command, append the result. No new I/O pattern.

  async function updateObjectiveDetails(
    objectiveId: ObjectiveId,
    changes: {
      readonly category?: ObjectiveCategory | null;
      readonly levelOfEffort?: ObjectiveLevelOfEffort | null;
      readonly priority?: ObjectivePriority | null;
      readonly reminder?: ObjectiveReminder | null;
      readonly cadenceGoalPerWeek?: number | null;
      readonly includeInWeeklyReview?: boolean;
      readonly surfaceOnToday?: boolean;
      readonly ambientRewardOnComplete?: boolean;
    },
  ): Promise<void> {
    const events = await allEvents();
    const { events: newEvents } = updateObjectiveDetailsCommand(events, objectiveId, changes, ports);
    await append(newEvents);
  }

  async function setObjectiveStatus(
    objectiveId: ObjectiveId,
    nextStatus: ObjectiveStatus,
  ): Promise<void> {
    const events = await allEvents();
    const { events: newEvents } = setObjectiveStatusCommand(events, objectiveId, nextStatus, ports);
    await append(newEvents);
  }

  async function setActionStatus(
    objectiveId: ObjectiveId,
    actionId: Parameters<DomainApi['setActionStatus']>[1],
    status: ActionStatus,
  ): Promise<void> {
    const events = await allEvents();
    const { events: newEvents } = setActionStatusCommand(events, objectiveId, actionId, status, ports);
    await append(newEvents);
  }

  async function linkObjectives(objectiveIdA: ObjectiveId, objectiveIdB: ObjectiveId): Promise<void> {
    const events = await allEvents();
    const { events: newEvents } = linkObjectivesCommand(events, objectiveIdA, objectiveIdB, ports);
    await append(newEvents);
  }

  async function unlinkObjectives(objectiveIdA: ObjectiveId, objectiveIdB: ObjectiveId): Promise<void> {
    const events = await allEvents();
    const { events: newEvents } = unlinkObjectivesCommand(events, objectiveIdA, objectiveIdB, ports);
    await append(newEvents);
  }

  async function setObjectivePinned(
    objectiveId: ObjectiveId,
    pinned: boolean,
    pinnedOrder: number | null,
  ): Promise<void> {
    const events = await allEvents();
    const { events: newEvents } = setObjectivePinnedCommand(
      events,
      objectiveId,
      pinned,
      pinnedOrder,
      ports,
    );
    await append(newEvents);
  }

  // ---------------------------------------------------------------------
  // Weekly review (docs/specs/phase-2c-weekly-review.md WS3/WS4)
  // ---------------------------------------------------------------------

  /** WR-9: records the accept with source 'review_suggestion' and awards xp.adjustmentAccepted (WR-17). */
  async function acceptSuggestion(
    suggestion: AdjustmentSuggestionView,
  ): Promise<CelebrationCue> {
    const events = await allEvents();
    const { events: newEvents } = acceptSuggestionCommand(events, suggestion, ports);
    const config = resolveRewardConfig(events);
    const liveBadges = assembleLiveBadges(events);
    const habitCategory = categoryOf(events, suggestion.habitId);
    await append(newEvents);
    return computeCelebrationCue(events, newEvents, config, clock.today(), {
      badges: liveBadges,
      habitCategory,
    });
  }

  async function acceptObjectiveDateSuggestion(
    suggestion: ObjectiveDateSuggestionView,
  ): Promise<CelebrationCue> {
    const events = await allEvents();
    const { events: newEvents } = acceptObjectiveDateSuggestionCommand(
      events,
      suggestion.objectiveId,
      suggestion.suggestedTargetDate,
      suggestion.reviewWeek,
      ports,
    );
    const config = resolveRewardConfig(events);
    const liveBadges = assembleLiveBadges(events);
    await append(newEvents);
    // Rewards v1 (RW-2): accepting a forecast suggestion (source:
    // 'forecast_suggestion', enforced by the command above) earns
    // xp.dateAdjustmentAccepted -- the realism bonus, "the smart play"
    // (PRD §8). A manual date edit (adjustObjectiveTargetDateManually)
    // never reaches this path (RW-3) and earns nothing. WR-13: unchanged
    // from Phase 1 -- the review screen calls this SAME command, not a
    // second implementation.
    return computeCelebrationCue(events, newEvents, config, clock.today(), { badges: liveBadges });
  }

  async function answerObjectiveCheckpoint(
    objectiveId: ObjectiveId,
    weekStart: WeekStart,
    answer: ObjectiveCheckpointAnswer,
  ): Promise<void> {
    const events = await allEvents();
    const { events: newEvents } = answerObjectiveCheckpointCommand(
      events,
      objectiveId,
      weekStart,
      answer,
      ports,
    );
    // WR-12/AC-W15: a status check-in, not an XP-bearing action -- no
    // celebration cue, matching the contract's void return type.
    await append(newEvents);
  }

  /**
   * WR-4/WR-15: records `review.completed` and awards xp.reviewCompleted,
   * regardless of on-time vs. late (WR-5/D16 -- no "late" branch exists
   * anywhere in this function or in xp.ts's computation of it). Re-completing
   * an already-completed week is IDEMPOTENT by design (dev-core-domain's
   * call, documented here): a second `review.completed` for the same
   * weekStart is appended (the event log is append-only, ADR-0002) but earns
   * ZERO further XP -- xp.ts's `computeReviewXpAwards`/
   * `computeWeeklyTargetBonusAwards` both collapse by `weekStart`, keeping
   * the FIRST (canonical order) event as the one that pays. This mirrors
   * `completeCheckin`'s existing collapse-by-day precedent (AC-B15) rather
   * than rejecting the second call outright -- a `review.completed` retry
   * (e.g. an optimistic-write retry after a flaky sync) must never throw or
   * silently double-pay.
   */
  async function completeReview(weekStart: WeekStart): Promise<CelebrationCue> {
    const events = await allEvents();
    const config = resolveRewardConfig(events);
    const liveBadges = assembleLiveBadges(events);
    const newEvent: ReviewCompletedEvent = {
      eventId: id.nextEventId(),
      type: 'review.completed',
      schemaVersion: 1,
      occurredOn: clock.today(),
      recordedAt: clock.now(),
      weekStart,
    };
    await append([newEvent]);
    // WR-16: xp.weeklyTargetBonus is recognized AT this same review.completed
    // event (see xp.ts's "TIMING DECISION" doc comment) -- diffing XP
    // before/after this one append already picks up reviewCompleted AND
    // every weeklyTargetBonus this week's habits earned, in one coalesced
    // cue (no separate toast per habit).
    return computeCelebrationCue(events, [newEvent], config, clock.today(), { badges: liveBadges });
  }

  // ---------------------------------------------------------------------
  // Rewards settings (RS3/RS7, Rewards v1)
  // ---------------------------------------------------------------------

  async function updateRewardConfig(config: RewardConfig): Promise<void> {
    const { events: newEvents } = updateRewardConfigCommand(config, ports);
    await append(newEvents);
  }

  async function resetRewardConfig(): Promise<void> {
    const { events: newEvents } = resetRewardConfigCommand(ports);
    await append(newEvents);
  }

  async function createBadge(draft: BadgeDraft): Promise<BadgeId> {
    const events = await allEvents();
    const { events: newEvents } = createBadgeCommand(events, draft, ports);
    await append(newEvents);
    const created = newEvents[0];
    if (!created || created.type !== 'badge.created') {
      throw new DomainError({ code: 'invalid_input', message: 'badge creation failed' });
    }
    return created.badgeId;
  }

  async function editBadge(
    badgeId: BadgeId,
    changes: Parameters<DomainApi['editBadge']>[1],
  ): Promise<void> {
    const events = await allEvents();
    const { events: newEvents } = editBadgeCommand(events, badgeId, changes, ports);
    await append(newEvents);
  }

  async function archiveBadge(badgeId: BadgeId): Promise<void> {
    const events = await allEvents();
    const { events: newEvents } = archiveBadgeCommand(events, badgeId, ports);
    await append(newEvents);
  }

  // ---------------------------------------------------------------------
  // Brain dump (R8.1, Phase 6)
  // ---------------------------------------------------------------------

  async function listNotes(): Promise<readonly NoteView[]> {
    const events = await allEvents();
    return listNotesReal(events);
  }

  async function createNote(text: string): Promise<NoteId> {
    const { events: newEvents } = createNoteCommand(text, ports);
    await append(newEvents);
    const created = newEvents[0];
    if (!created || created.type !== 'note.created') {
      throw new DomainError({ code: 'invalid_input', message: 'note creation failed' });
    }
    return created.noteId;
  }

  async function editNote(noteId: NoteId, text: string): Promise<void> {
    const events = await allEvents();
    const { events: newEvents } = editNoteCommand(events, noteId, text, ports);
    await append(newEvents);
  }

  async function deleteNote(noteId: NoteId): Promise<void> {
    const events = await allEvents();
    const { events: newEvents } = deleteNoteCommand(events, noteId, ports);
    await append(newEvents);
  }

  /**
   * The only bridge from a note into actionable work (spec §2.3, AC-T2).
   * Sequencing per the ADR addendum: the target-creating event is appended
   * FIRST (via the existing addAction/createPriority command, so cap/
   * existence validation is never duplicated here), THEN `note.promoted`
   * referencing the resulting ID -- both in the SAME batch, so a reader
   * never observes the promotion event before its target in log order.
   */
  async function promoteNote(
    noteId: NoteId,
    target:
      | { readonly kind: 'action'; readonly objectiveId: ObjectiveId }
      | { readonly kind: 'priority' },
  ): Promise<NotePromotionTarget> {
    const events = await allEvents();
    const note = listNotesReal(events).find((n) => n.noteId === noteId);
    if (!note) {
      throw new DomainError({ code: 'note_not_found', message: `note ${noteId} not found` });
    }

    if (target.kind === 'action') {
      const { events: actionEvents } = addActionCommand(
        events,
        target.objectiveId,
        { title: note.text, dueDate: null, milestoneTag: null, position: null },
        ports,
      );
      const added = actionEvents[0];
      if (!added || added.type !== 'action.added') {
        throw new DomainError({ code: 'invalid_input', message: 'action creation failed' });
      }
      const { events: promotedEvents } = promoteNoteCommand(
        [...events, ...actionEvents],
        noteId,
        { kind: 'action', objectiveId: target.objectiveId, actionId: added.actionId },
        ports,
      );
      await append([...actionEvents, ...promotedEvents]);
      return { kind: 'action', objectiveId: target.objectiveId, actionId: added.actionId };
    }

    const { events: priorityEvents } = createPriorityCommand(
      events,
      clock.today(),
      { title: note.text, linkedAction: null, timeLabel: null },
      ports,
    );
    const created = priorityEvents[0];
    if (!created || created.type !== 'priority.created') {
      throw new DomainError({ code: 'invalid_input', message: 'priority creation failed' });
    }
    const { events: promotedEvents } = promoteNoteCommand(
      [...events, ...priorityEvents],
      noteId,
      { kind: 'priority', priorityId: created.priorityId },
      ports,
    );
    await append([...priorityEvents, ...promotedEvents]);
    return { kind: 'priority', priorityId: created.priorityId };
  }

  // ---------------------------------------------------------------------
  // Priorities (R8.2, Phase 6)
  // ---------------------------------------------------------------------

  async function listPriorities(date: IsoDate): Promise<readonly PriorityView[]> {
    const events = await allEvents();
    return listPrioritiesReal(events, date);
  }

  async function createPriority(
    date: IsoDate,
    input: {
      readonly title: string;
      readonly linkedAction: PriorityActionLink | null;
      readonly timeLabel: DisplayTimeLabel | null;
    },
  ): Promise<PriorityId> {
    const events = await allEvents();
    const { events: newEvents } = createPriorityCommand(events, date, input, ports);
    await append(newEvents);
    const created = newEvents[0];
    if (!created || created.type !== 'priority.created') {
      throw new DomainError({ code: 'invalid_input', message: 'priority creation failed' });
    }
    return created.priorityId;
  }

  /**
   * Reconciliation dispatch (ADR addendum's D-Priority): a LINKED priority
   * routes to the exact same `completeActionCommand` path `completeAction`
   * uses -- never appends `priority.completed`. A STANDALONE priority
   * appends `priority.completed` and earns `xp.priorityCompleted`.
   */
  async function completePriority(priorityId: PriorityId, date: IsoDate): Promise<CelebrationCue> {
    const events = await allEvents();
    const path = resolvePriorityCompletionPath(events, priorityId);
    const config = resolveRewardConfig(events);
    const liveBadges = assembleLiveBadges(events);

    if (path.kind === 'linked') {
      assertValidRecordingDate(date, clock);
      const { events: newEvents } = completeActionCommand(
        events,
        path.link.objectiveId,
        path.link.actionId,
        date,
        ports,
      );
      await append(newEvents);
      return computeCelebrationCue(events, newEvents, config, clock.today(), { badges: liveBadges });
    }

    const { events: newEvents } = completeStandalonePriorityCommand(events, priorityId, date, ports);
    await append(newEvents);
    return computeCelebrationCue(events, newEvents, config, clock.today(), { badges: liveBadges });
  }

  async function removePriority(priorityId: PriorityId): Promise<void> {
    const events = await allEvents();
    const { events: newEvents } = removePriorityCommand(events, priorityId, ports);
    await append(newEvents);
  }

  // ---------------------------------------------------------------------
  // Ideas (R8.4, Phase 6)
  // ---------------------------------------------------------------------

  async function listIdeas(): Promise<readonly IdeaView[]> {
    const events = await allEvents();
    return listIdeasReal(events);
  }

  async function getIdeaCounts(today: IsoDate): Promise<IdeaCountsView> {
    const events = await allEvents();
    return getIdeaCountsReal(events, today);
  }

  async function createIdea(text: string, category: IdeaCategory): Promise<IdeaId> {
    const { events: newEvents } = createIdeaCommand(text, category, ports);
    await append(newEvents);
    const created = newEvents[0];
    if (!created || created.type !== 'idea.created') {
      throw new DomainError({ code: 'invalid_input', message: 'idea creation failed' });
    }
    return created.ideaId;
  }

  async function deleteIdea(ideaId: IdeaId): Promise<void> {
    const events = await allEvents();
    const { events: newEvents } = deleteIdeaCommand(events, ideaId, ports);
    await append(newEvents);
  }

  /** No promote-to-Priority path exists for ideas (AC-T12) -- enforced by this method's own signature (objectiveId only). */
  async function promoteIdea(ideaId: IdeaId, objectiveId: ObjectiveId): Promise<ActionId> {
    const events = await allEvents();
    const idea = listIdeasReal(events).find((i) => i.ideaId === ideaId);
    if (!idea) {
      throw new DomainError({ code: 'idea_not_found', message: `idea ${ideaId} not found` });
    }
    const { events: actionEvents } = addActionCommand(
      events,
      objectiveId,
      { title: idea.text, dueDate: null, milestoneTag: null, position: null },
      ports,
    );
    const added = actionEvents[0];
    if (!added || added.type !== 'action.added') {
      throw new DomainError({ code: 'invalid_input', message: 'action creation failed' });
    }
    const { events: promotedEvents } = promoteIdeaCommand(
      [...events, ...actionEvents],
      ideaId,
      objectiveId,
      added.actionId,
      ports,
    );
    await append([...actionEvents, ...promotedEvents]);
    return added.actionId;
  }

  // ---------------------------------------------------------------------
  // Sentence of the day (R8.7, Phase 6)
  // ---------------------------------------------------------------------

  async function getSentenceOfDayState(quoteId: string): Promise<SentenceOfDayState> {
    const events = await allEvents();
    return getSentenceOfDayStateReal(events, quoteId);
  }

  async function saveQuote(quoteId: string): Promise<void> {
    const { events: newEvents } = saveQuoteCommand(quoteId, ports);
    await append(newEvents);
  }

  // ---------------------------------------------------------------------
  // Next up / trailing analytics (Phase 6, read-only)
  // ---------------------------------------------------------------------

  async function getNextUp(tomorrow: IsoDate): Promise<NextUpView> {
    const events = await allEvents();
    return getNextUpReal(events, tomorrow);
  }

  async function getTrailingAnalytics(today: IsoDate): Promise<TrailingAnalyticsView> {
    const events = await allEvents();
    return getTrailingAnalyticsReal(events, today);
  }

  // ---------------------------------------------------------------------
  // Extended check-in scalars (R8.5/R8.3/R8.6/R8.9, Phase 6)
  // ---------------------------------------------------------------------

  async function setSleepHours(date: IsoDate, hours: number): Promise<void> {
    assertValidRecordingDate(date, clock);
    const { events: newEvents } = setSleepHoursCommand(date, hours, ports);
    await append(newEvents);
  }

  async function setFavoriteMoment(
    date: IsoDate,
    input: { readonly tags: readonly LifeAreaTag[]; readonly reflection: string },
  ): Promise<void> {
    assertValidRecordingDate(date, clock);
    const { events: newEvents } = setFavoriteMomentCommand(date, input, ports);
    await append(newEvents);
  }

  async function setGratitudeSlot(date: IsoDate, slot: 0 | 1 | 2, text: string): Promise<void> {
    assertValidRecordingDate(date, clock);
    const events = await allEvents();
    const { events: newEvents } = setGratitudeSlotCommand(events, date, slot, text, ports);
    await append(newEvents);
  }

  /** R8.9: idempotent (AC-T21), non-gating (AC-T22), fully-paid-late (AC-T23). */
  async function closeDay(date: IsoDate): Promise<CelebrationCue> {
    assertValidRecordingDate(date, clock);
    const events = await allEvents();
    const config = resolveRewardConfig(events);
    const liveBadges = assembleLiveBadges(events);
    const { events: newEvents } = closeDayCommand(date, ports);
    await append(newEvents);
    return computeCelebrationCue(events, newEvents, config, clock.today(), { badges: liveBadges });
  }

  // ---------------------------------------------------------------------
  // Data ownership (R5.2) and app state
  // ---------------------------------------------------------------------

  async function exportData(): Promise<ExportFile> {
    const events = await allEvents();
    return {
      format: 'compass-export',
      formatVersion: 1,
      exportedAt: clock.now(),
      events,
    };
  }

  /**
   * Import is REPLACE, never merge (ADR-0004 addendum, import-replace ruling
   * 2026-07-03; PRD v0.17 §8; phase-2e-nav-settings-export.md §4.3 IM-5):
   * the local log ends up as exactly the (deduped) contents of `file`, full
   * stop -- never a union of old-local-plus-imported. `cache.clearLocal()`
   * erases the existing log, outbox, and cursor FIRST; the file's own events
   * (deduped by eventId, first occurrence wins -- defends a hand-edited or
   * corrupt file) are then replayed through the normal `append` path so they
   * re-enqueue for push like any other locally-originated write. `duplicates`
   * now counts eventId collisions WITHIN the file itself (a malformed-file
   * diagnostic), not overlap with prior local state -- there is no
   * "prior local state" left to overlap with by the time replay happens.
   *
   * Known, accepted consequence (documented in the ADR, not silently
   * assumed): this is a LOCAL replace only. Deletion never propagates
   * (ADR-0004 "nothing deletes") -- if the D1 server or another device still
   * holds events absent from the imported file, the next successful sync's
   * pull (cursor was just reset to 0) re-fetches the server's full log and
   * can reintroduce them. Correct and intended for the feature's actual
   * purpose (disaster recovery / device migration onto a file that is
   * normally a full superset of local state, PRD v0.17 §8) -- not a
   * multi-device merge tool.
   */
  async function importData(file: ExportFile): Promise<ImportSummary> {
    const deduped = dedupeEvents(file.events);
    const duplicates = file.events.length - deduped.length;

    await cache.clearLocal();
    await append(deduped);

    return { imported: deduped.length, duplicates };
  }

  async function completeOnboarding(): Promise<void> {
    const event = {
      eventId: id.nextEventId(),
      type: 'onboarding.completed' as const,
      schemaVersion: 1 as const,
      occurredOn: clock.today(),
      recordedAt: clock.now(),
    };
    await append([event]);
  }

  // ---------------------------------------------------------------------
  // Reactivity and sync
  // ---------------------------------------------------------------------

  function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  }

  async function syncNow(): Promise<void> {
    await syncQueue.syncNow();
    notify();
  }

  const api: DomainApi = {
    getDailyPage,
    getWeeklyReview,
    getCurrentReviewWeek,
    listHabits,
    listObjectives,
    getObjective,
    listBadges,
    getRewardSnapshot,
    getRewardsExplainer,
    getRewardConfig,
    getCompletionHistory,
    getCompletionHistoryYearSummary,
    getSyncStatus,
    isOnboardingNeeded,
    listPinnedObjectives,

    listNotes,
    createNote,
    editNote,
    deleteNote,
    promoteNote,

    listPriorities,
    createPriority,
    completePriority,
    removePriority,

    listIdeas,
    getIdeaCounts,
    createIdea,
    deleteIdea,
    promoteIdea,

    getSentenceOfDayState,
    saveQuote,

    getNextUp,
    getTrailingAnalytics,

    completeHabit,
    skipHabit,
    logClean,
    logLapse,
    logStudySession,
    revertLogEntry,
    setMood,
    setEnergy,
    setNote,
    completeCheckin,
    setSleepHours,
    setFavoriteMoment,
    setGratitudeSlot,
    closeDay,

    createHabit,
    editHabit,
    adjustTargetManually,
    pauseHabit,
    resumeHabit,
    archiveHabit,

    createObjective,
    editObjective,
    updateObjectiveDetails,
    adjustObjectiveTargetDateManually,
    setObjectiveStatus,
    archiveObjective,
    addAction,
    editAction,
    removeAction,
    completeAction,
    setActionStatus,
    reorderActions,
    linkObjectives,
    unlinkObjectives,
    setObjectivePinned,

    acceptSuggestion,
    acceptObjectiveDateSuggestion,
    answerObjectiveCheckpoint,
    completeReview,

    updateRewardConfig,
    resetRewardConfig,
    createBadge,
    editBadge,
    archiveBadge,

    exportData,
    importData,
    completeOnboarding,

    subscribe,
    syncNow,

    getAllEvents: allEvents,
  };

  return { api, waitForPendingSync };
}

// Referenced only for its type in a few command wrappers above; re-exported
// so callers constructing options do not need a separate import.
export type { HabitCategory };
